import React, { useState, useEffect } from "react";
import { Alert } from "react-bootstrap";
import { FiPlus } from "react-icons/fi";
import { getCDSettlements, getAllBankDetails } from "../../services/adminApi";
import CreateSettlementPage from "./CreateSettlementPage";
import SettlementDetailsView from "./components/Settlement/SettlementDetailsView";
import SettlementFilters from "./components/Settlement/SettlementFilters";
import SettlementMetrics from "./components/Settlement/SettlementMetrics";
import SettlementListTable from "./components/Settlement/SettlementListTable";
import "./PaymentSettlementCustom.css";

const AccountantSettlementPage = () => {
  const [viewMode, setViewMode] = useState("list");
  const [selectedSettlement, setSelectedSettlement] = useState(null);

  const [settlements, setSettlements] = useState([]);
  const [castingDirectors, setCastingDirectors] = useState([]);
  const [metrics, setMetrics] = useState({
    total_settlements: 0,
    total_amount: 0,
    settlement_initiated: 0,
    settlement_completed: 0,
    failed_cancelled: 0,
  });

  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState("");

  const [castingDirectorFilter, setCastingDirectorFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");

  useEffect(() => {
    getAllBankDetails()
      .then((res) => {
        if (res.data?.success) setCastingDirectors(res.data.data || []);
      })
      .catch((err) => console.error("Error loading casting directors list:", err));
  }, []);

  useEffect(() => {
    if (viewMode === "list") {
      fetchSettlements();
    }
  }, [viewMode, castingDirectorFilter, statusFilter, typeFilter]);

  const fetchSettlements = async () => {
    try {
      setLoading(true);
      setErrorMsg("");
      const res = await getCDSettlements({
        castingDirectorId: castingDirectorFilter,
        status: statusFilter,
        type: typeFilter,
      });

      if (res.data?.success) {
        setSettlements(res.data.data || []);
        if (res.data.metrics) {
          setMetrics(res.data.metrics);
        }
      } else {
        setErrorMsg("Failed to fetch settlements.");
      }
    } catch (err) {
      console.error(err);
      setErrorMsg("Failed to load settlements list from server.");
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    if (status === "Settlement Completed" || status === "Paid") {
      return <span className="bma-badge badge-success">Completed</span>;
    }
    if (status === "Settlement Initiated" || status === "Initiated") {
      return <span className="bma-badge badge-warning">Initiated</span>;
    }
    if (status === "Failed" || status === "Cancelled") {
      return <span className="bma-badge badge-danger">{status}</span>;
    }
    return <span className="bma-badge badge-warning">{status || "Processing"}</span>;
  };

  const handleResetFilters = () => {
    setCastingDirectorFilter("");
    setStatusFilter("");
    setTypeFilter("");
  };

  if (viewMode === "create") {
    return (
      <CreateSettlementPage
        onBack={() => setViewMode("list")}
        onSuccess={() => setViewMode("list")}
      />
    );
  }

  if (viewMode === "details") {
    return (
      <SettlementDetailsView
        settlementId={selectedSettlement?.id}
        onBack={() => setViewMode("list")}
      />
    );
  }

  return (
    <div className="bma-settlement-page">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <div className="bma-breadcrumb">
            Home &nbsp;&gt;&nbsp; Payment Settlements &nbsp;&gt;&nbsp; <strong>Settlement Master</strong>
          </div>
          <h1 className="bma-page-title">Settlement Master</h1>
          <p className="bma-page-subtitle">Create and manage settlements for casting directors and track payment status.</p>
        </div>
        <div className="d-flex gap-2">
          <button type="button" className="bma-btn-red" onClick={() => setViewMode("create")}>
            <FiPlus /> Create Settlement
          </button>
        </div>
      </div>

      {errorMsg && <Alert variant="danger">{errorMsg}</Alert>}

      {/* FILTER COMPONENT */}
      <SettlementFilters
        castingDirectors={castingDirectors}
        castingDirectorFilter={castingDirectorFilter}
        setCastingDirectorFilter={setCastingDirectorFilter}
        statusFilter={statusFilter}
        setStatusFilter={setStatusFilter}
        typeFilter={typeFilter}
        setTypeFilter={setTypeFilter}
        onReset={handleResetFilters}
        onApply={fetchSettlements}
      />

      {/* METRICS CARDS COMPONENT */}
      <SettlementMetrics metrics={metrics} />

      {/* SETTLEMENT TABLE COMPONENT */}
      <SettlementListTable
        loading={loading}
        settlements={settlements}
        onViewDetails={(row) => {
          setSelectedSettlement(row);
          setViewMode("details");
        }}
        getStatusBadge={getStatusBadge}
      />
    </div>
  );
};

export default AccountantSettlementPage;